import { gql, GraphQLClient } from 'graphql-request';

import { getAssetFromAddress, getContractAddress } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import { EVestaAsset } from '~config/types';
import { getAllQuery } from '~services/subgraph/queries';

import { GraphSystemVault } from '../types';

export default class Vaults {
  constructor(private readonly client: GraphQLClient, private readonly networkId: NetworkIds) {}

  // @TODO get interest rate from graph
  async getSystemVaultInfo() {
    const globalStatsQuery = gql`
      query get_global_stats {
        assetStats(first: 1000) {
          id
          currentAssetSystemState {
            totalDebt
            totalCollateral
            totalCollateralRatio
          }
        }
      }
    `;

    const res: { assetStats: GraphSystemVault & { id: string }[] } = await this.client.request(
      globalStatsQuery,
    );
    return res.assetStats.reduce(
      (acc, { id, ...assetStats }) => ({
        ...acc,
        [getAssetFromAddress(id, this.networkId)]: assetStats,
      }),
      {} as Record<EVestaAsset, GraphSystemVault>,
    );
  }

  async getAllAssetVaults(asset: EVestaAsset, network: NetworkIds) {
    const id = getContractAddress(asset, network).toLowerCase();

    const assetVaultsQuery = gql`
      query get_asset_troves($id: ID, $skip: Int, $first: Int) {
        asset(id: $id) {
          troves(first: $first, skip: $skip, where: { status: open }) {
            debt
            collateral
            owner {
              id
            }
          }
        }
      }
    `;

    const assetPriceQuery = gql`
      query get_asset_price($id: ID) {
        asset(id: $id) {
          assetStat {
            currentAssetSystemState {
              price
            }
          }
        }
      }
    `;

    const [assetVaults, assetPrice] = await Promise.all([
      getAllQuery<{ debt: string; collateral: string; owner: { id: string } }>({
        query: assetVaultsQuery,
        client: this.client,
        queryParams: {
          id,
        },
        combineKey: 'asset.troves',
        queryLength: 1000,
      }),
      this.client.request<{ asset: { assetStat: { currentAssetSystemState: { price: string } } } }, any>(
        assetPriceQuery,
        { id },
      ),
    ]);

    return assetVaults
      .map(({ debt, collateral, owner: { id: ownerId } }) => {
        return {
          collateral: Number(collateral),
          debt: Number(debt),
          owner: ownerId,
          collRatio:
            (Number(collateral) * Number(assetPrice.asset.assetStat.currentAssetSystemState.price)) /
            Number(debt),
        };
      })
      .sort((a, b) => a.collRatio - b.collRatio);
  }
}
